'use client';

// Removed unused imports
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { usePerformanceDebug } from '@/lib/debug-performance-hook';
import { stripHtmlServer } from '@/lib/utils';
import { getOptimizedImageUrl } from '@/lib/image-utils';

// Category mapping
const categoryUrlToDisplayName: { [key: string]: string } = {
  'image-generation': 'Image Generation',
  'music-audio': 'Music / Audio',
  'website-app': 'Website / App',
  'graphic-design': 'Graphic Design',
  'text-copywriting': 'Text / Copywriting',
  '3d': '3D',
  'video-film': 'Video / Film',
  'presentation': 'Presentation',
  'animation': 'Animation',
  'coding': 'Coding'
};

interface Tool {
  id: string;
  databaseId: number;
  title: string;
  slug: string;
  excerpt: string;
  featuredImage?: {
    node: {
      sourceUrl: string;
      altText?: string;
    };
  };
  toolFields?: {
    toolLogo?: {
      node?: {
        sourceUrl: string;
        altText?: string;
        mediaItemUrl?: string;
      };
    };
  };
  toolCategories?: {
    nodes: Array<{
      id: string;
      name: string;
      slug: string;
    }>;
  };
}

interface ToolsGridProps {
  tools: Tool[];
}

export default function ToolsGrid({ tools }: ToolsGridProps) {
  usePerformanceDebug('ToolsGrid', 'TOOLS_PAGE');
  
  const searchParams = useSearchParams();
  const categoryParam = searchParams.get('category');
  const searchQuery = searchParams.get('search') || '';
  
  // Determine selected category from URL
  const selectedCategory = categoryParam && categoryUrlToDisplayName[categoryParam] 
    ? categoryUrlToDisplayName[categoryParam] 
    : 'All';
  
  // Filter tools based on category and search
  const filteredTools = tools.filter(tool => {
    // Category filter
    if (selectedCategory !== 'All') {
      const hasCategory = tool.toolCategories?.nodes.some(
        cat => cat.name === selectedCategory
      );
      if (!hasCategory) return false;
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesTitle = tool.title.toLowerCase().includes(query);
      const matchesExcerpt = tool.excerpt?.toLowerCase().includes(query);
      const matchesCategory = tool.toolCategories?.nodes.some(
        cat => cat.name.toLowerCase().includes(query)
      );
      
      if (!matchesTitle && !matchesExcerpt && !matchesCategory) {
        return false;
      }
    }
    
    return true;
  });
  
  return (
    <div className="w-full flex justify-center bg-[#f8f8f8]">
      <section className="relative w-full max-w-[1440px] py-12 px-6 sm:px-8 lg:px-12">
        {/* Results count and sort */}
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-[18px] font-medium text-gray-700">
            {filteredTools.length} {filteredTools.length === 1 ? 'Tool' : 'Tools'} Found
          </h2>
          <div className="text-[14px] text-gray-500">
            Sorted by relevance
          </div>
        </div>
        
        {/* Tools Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTools.map((tool) => (
            <Link 
              key={tool.id} 
              href={`/tools/${tool.slug}`}
              className="group block bg-white border border-gray-200 hover:border-black transition-all overflow-hidden"
            >
              <div className="flex h-[200px]">
                {/* Image Container - Left Side */}
                <div className="w-[200px] h-full bg-gray-100 flex-shrink-0 relative overflow-hidden">
                  {(() => {
                    const imageUrl = 
                      tool.featuredImage?.node?.sourceUrl || 
                      tool.toolFields?.toolLogo?.node?.sourceUrl ||
                      tool.toolFields?.toolLogo?.node?.mediaItemUrl;
                    
                    const altText = 
                      tool.featuredImage?.node?.altText || 
                      tool.toolFields?.toolLogo?.node?.altText || 
                      tool.title;
                    
                    if (imageUrl) {
                      return (
                        <Image
                          src={getOptimizedImageUrl(imageUrl, 200, 200)}
                          alt={altText}
                          fill
                          className="object-contain p-4"
                          sizes="200px"
                        />
                      );
                    }
                    
                    return (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <span className="text-4xl font-bold text-gray-300">
                          {tool.title.charAt(0)}
                        </span>
                      </div>
                    );
                  })()}
                </div>
                
                {/* Content Container - Right Side */}
                <div className="flex-1 p-6 flex flex-col">
                  {/* Tool Name */}
                  <h3 className="font-ibm-plex-sans-condensed font-medium text-[20px] tracking-[1.8px] uppercase text-black mb-2">
                    {tool.title}
                  </h3>
                  
                  {/* Excerpt - with HTML stripped */}
                  <p className="text-[14px] text-gray-600 line-clamp-2 mb-4 flex-grow">
                    {stripHtmlServer(tool.excerpt)}
                  </p>
                  
                  {/* Categories - aligned to bottom */}
                  {tool.toolCategories?.nodes && tool.toolCategories.nodes.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-auto">
                      {tool.toolCategories.nodes.map((category) => (
                        <span
                          key={category.id}
                          className="inline-block px-3 py-1 bg-gray-100 border border-gray-300 text-[10px] font-ibm-plex-sans-condensed tracking-[1px] uppercase text-gray-700"
                        >
                          {category.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
        
        {/* No results */}
        {filteredTools.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-[18px]">
              No tools found matching your criteria.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}